import { CheckCircle } from 'lucide-react'

function PPH2({ children }) {
  return (
    <div className="mt-14 mb-5 pt-10 border-t border-gray-200">
      <h2 className="flex items-center gap-3 text-2xl font-bold text-gray-900">
        <span className="flex-shrink-0 w-1 h-7 rounded-full bg-gradient-to-b from-primary-500 to-accent-500" />
        {children}
      </h2>
    </div>
  )
}

function PPH3({ children }) {
  return (
    <h3 className="mt-8 mb-3 pl-4 border-l-2 border-accent-500 text-lg font-semibold text-gray-900">
      {children}
    </h3>
  )
}

function PPItem({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-gray-600 leading-relaxed">
      <CheckCircle className="w-4 h-4 text-accent-500 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  )
}

function AppendixTableRow({ cols }) {
  return (
    <tr className="hover:bg-gray-50/60 transition-colors">
      {cols.map((c, i) => (
        <td key={i} className="px-4 py-3.5 text-sm text-gray-600 border-b border-gray-100 align-top">
          {c}
        </td>
      ))}
    </tr>
  )
}

export function PrivacyPolicyBody() {
  return (
    <article>
      <p className="text-gray-600 leading-relaxed">
        This Privacy Policy describes how we collect, use, disclose, retain, and protect information when you
        interact with Rithmo.
      </p>

      <section>
        <PPH2>1. Introduction</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Rithmo, Inc. (&quot;Rithmo,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) provides an AI-enabled
          meeting governance platform that helps organizations coordinate meetings, transcribe meeting activity,
          generate meeting outputs, analyze meeting effectiveness, and support meeting-related workflows.
        </p>
        <p className="mb-4 text-gray-600 leading-relaxed">
          This Privacy Policy explains how we collect, use, disclose, retain, and protect information when you
          visit our website, use our applications, participate in meetings where Rithmo is enabled, connect
          third-party services, or otherwise interact with Rithmo.
        </p>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Rithmo is designed for organizations that want the benefits of AI-assisted meeting operations while
          maintaining control over sensitive meeting content, business context, and customer-facing outputs.
          Because our service may process meeting audio, transcripts, agenda content, calendar metadata, meeting
          outputs, and other sensitive business information, we use that information for defined product,
          security, compliance, and customer-authorized purposes.
        </p>
      </section>

      <section>
        <PPH2>2. Scope of This Policy</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">This Privacy Policy applies to information processed through:</p>
        <ul className="my-4 space-y-2">
          <PPItem>the Rithmo website;</PPItem>
          <PPItem>the Rithmo web dashboard;</PPItem>
          <PPItem>the Rithmo in-meeting experience;</PPItem>
          <PPItem>integrations with meeting, calendar, transcription, AI, communication, storage, and workflow providers;</PPItem>
          <PPItem>customer support, sales, security, and administrative interactions; and</PPItem>
          <PPItem>product telemetry, logs, and analytics associated with the Rithmo service.</PPItem>
        </ul>
        <p className="mb-4 text-gray-600 leading-relaxed">
          For business customers, Rithmo generally acts as a service provider or processor for Customer Content
          that we process on behalf of the customer. The customer controls how its authorized users use the
          service, what meetings are connected, what integrations are enabled, and what retention or administrative
          settings apply where supported.
        </p>
        <p className="mb-4 text-gray-600 leading-relaxed">
          If you use Rithmo through your employer or another organization, that organization may be responsible
          for determining how your information is processed through the service. You should contact that
          organization for questions about its internal use of Rithmo.
        </p>
      </section>

      <section>
        <PPH2>3. Key Definitions</PPH2>
        <ul className="my-4 space-y-4 text-gray-600">
          <li>
            <span className="font-semibold text-gray-900">Account Data </span>
            means information used to create, manage, secure, and support a Rithmo account, such as name, email
            address, organization, role, authentication data, and account settings.
          </li>
          <li>
            <span className="font-semibold text-gray-900">Customer Content </span>
            means content submitted to, captured by, generated through, or processed by Rithmo on behalf of a
            customer such as meeting audio, transcripts, segments, speaker labels, meeting chat or messages where
            enabled, agenda content, calendar metadata, uploaded documents, summaries, decisions, action items,
            follow-ups, analytics, AI outputs, and related artifacts.
          </li>
          <li>
            <span className="font-semibold text-gray-900">Meeting Data </span>
            means Customer Content and metadata for a meeting, including identifiers, title, agenda, duration,
            participants, speaker activity, transcript data, bot status, outputs, and workflow data.
          </li>
          <li>
            <span className="font-semibold text-gray-900">Derived Data </span>
            means information generated from Customer Content (summaries, action items, decisions, scorecards,
            analytics, recommendations, longitudinal customer-scoped learning records).
          </li>
          <li>
            <span className="font-semibold text-gray-900">Usage Data </span>
            means how users and systems interact with Rithmo: logs, browser type, IP, device info, timestamps,
            feature usage, errors, latency, security telemetry.
          </li>
          <li>
            <span className="font-semibold text-gray-900">AI Provider </span>
            means a third-party that supplies AI/ML, speech-to-text, TTS, embeddings, summarization, classification,
            or related model capabilities for Rithmo.
          </li>
        </ul>
      </section>

      <section>
        <PPH2>4. Information We Collect</PPH2>

        <PPH3>4.1 Account Data</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We may collect Account Data when you create an account, authenticate, join a workspace, interact with
          support, or administer the service:
        </p>
        <ul className="my-4 space-y-2">
          <PPItem>name;</PPItem>
          <PPItem>email address;</PPItem>
          <PPItem>password or credential information where that sign-in mode is enabled;</PPItem>
          <PPItem>organization or workspace affiliation;</PPItem>
          <PPItem>role, permissions, and administrative settings;</PPItem>
          <PPItem>
            identifiers or profile-aligned fields from third-party services when you authorize OAuth integrations
            (for example calendar or meeting-platform connections), limited to what those providers disclose;
          </PPItem>
          <PPItem>billing or subscription contacts where applicable; and</PPItem>
          <PPItem>support communications and related metadata.</PPItem>
        </ul>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Depending on deployment, sign-in may include email/password flows, bearer or session credentials, or
          other mechanisms your administrator enables.
        </p>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We do not intentionally store reusable raw passwords except as strong one-way cryptographic hashes where
          modern password authentication applies. Secrets are handled using appropriate safeguards.
        </p>

        <PPH3>4.2 Meeting Data and Customer Content</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">We may process Meeting Data including:</p>
        <ul className="my-4 space-y-2">
          <PPItem>titles, identifiers, join info, agenda, type, scheduled time, duration, recurrence, location, organizer;</PPItem>
          <PPItem>participant names, speaker labels, attendance and activity;</PPItem>
          <PPItem>meeting audio where transcription, bot, recording, or speech features are enabled;</PPItem>
          <PPItem>transcripts, segments, timestamps, speaker turns, transcription metadata;</PPItem>
          <PPItem>decisions, action items, follow-ups, summaries, recaps, scorecards, outputs;</PPItem>
          <PPItem>meeting chat or messages where enabled;</PPItem>
          <PPItem>uploaded or linked documents where document features are enabled;</PPItem>
          <PPItem>customer policy, governance, and workflow configuration; and</PPItem>
          <PPItem>Derived Data from activity.</PPItem>
        </ul>
        <p className="mb-4 text-gray-600 leading-relaxed">
          What we process depends on enabled features, integrations, and settings.
        </p>

        <PPH3>4.3 Calendar and Integration Data</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">If you connect third-party services, we may process:</p>
        <ul className="my-4 space-y-2">
          <PPItem>OAuth codes, tokens, refresh tokens, credentials;</PPItem>
          <PPItem>calendar IDs, titles, times, attendees, organizers, locations, descriptions;</PPItem>
          <PPItem>Zoom or platform IDs, lifecycle events, bot or moderation state;</PPItem>
          <PPItem>transcript/speech/bot events from ingestion providers;</PPItem>
          <PPItem>messaging/email/workflow metadata where integrations enable it;</PPItem>
          <PPItem>sync state, integration status, errors, operational logs.</PPItem>
        </ul>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Integration data is used for the integration and related security, debugging, support, and compliance.
        </p>

        <PPH3>4.4 Usage Data, Logs, and Security Data</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">We collect Usage Data such as:</p>
        <ul className="my-4 space-y-2">
          <PPItem>IP address, browser, OS, identifiers;</PPItem>
          <PPItem>pages, features, actions;</PPItem>
          <PPItem>timestamps, request metadata, errors, performance;</PPItem>
          <PPItem>authentication and security events;</PPItem>
          <PPItem>API and model usage, token counts where available, latency, fallbacks, provider health; and</PPItem>
          <PPItem>audit logs for configuration, integrations, admin actions.</PPItem>
        </ul>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We aim to minimize unnecessarily sensitive payloads in routine logs. Troubleshooting, incidents,
          customer-approved support, or narrowly scoped QA may capture excerpts where needed depending on the
          environment and agreements.
        </p>

        <PPH3>4.5 Website and Marketing Data</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We may collect contacts, demo requests, usage of our marketing site, device data, and communication
          preferences. We use cookies or similar technologies for essential site functions, analytics, performance,
          or marketing consistency with your settings where applicable.
        </p>
        <p className="text-gray-500 text-sm">
          For the current named vendors (analytics, CRM, pixels, automation), contact privacy@rithmo.ai. We can
          provide an updated list on request.
        </p>
      </section>

      <section>
        <PPH2>5. How We Use Information</PPH2>

        <PPH3>5.1 Provide and Operate the Service</PPH3>
        <ul className="my-4 space-y-2">
          <PPItem>accounts and authentication;</PPItem>
          <PPItem>integrations and meetings;</PPItem>
          <PPItem>audio, transcripts, agendas, metadata;</PPItem>
          <PPItem>outputs and dashboard/in-meeting experience;</PPItem>
          <PPItem>governance workflows and support.</PPItem>
        </ul>

        <PPH3>5.2 AI-Enabled Features</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We use Customer Content and Derived Data for transcription, summaries, facilitation, extraction,
          recommendations, document intelligence, embeddings, TTS, and related features, sometimes through external
          providers under contracts and controls designed to protect Customer Content.
        </p>

        <PPH3>5.3 Security and Abuse Prevention</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Fraud and abuse detection, auth verification, workspace protection, reliability monitoring, and terms
          enforcement.
        </p>

        <PPH3>5.4 Improve and Develop</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Usage Data, feedback, support, configuration, aggregates. We do not use Customer Content to train
          general-purpose models except where expressly covered in posted terms or a separate written program
          with controls.
        </p>

        <PPH3>5.5 Communicate</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Administrative notices, updates, alerts, billing, announcements related to Rithmo.
        </p>

        <PPH3>5.6 Legal and Contractual</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">Compliance, agreements, lawful requests, rights and safety.</p>
      </section>

      <section>
        <PPH2>6. AI and External Model Providers</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Rithmo may use external AI, speech, bot, and infrastructure providers. Principles:
        </p>

        <PPH3>6.1 Purpose Limitation</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Customer Content goes to providers to deliver, secure, support, or improve enabled features per contract
          and lawful instructions unless otherwise authorized.
        </p>

        <PPH3>6.2 Training Posture</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We do not rely on identifiable Customer Content to train general-purpose models in ordinary operations.
          Vendor APIs, enterprise settings, and written agreements are selected to narrow provider reuse;
          validate your stack with counsel.
        </p>

        <PPH3>6.3 Minimization</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">We aim to send only what the task reasonably requires.</p>

        <PPH3>6.4 Scrubbing</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Where feasible we minimize/redact/pseudonymize before external inference. Controls vary by feature,
          routing, prompting maturity, and deployment.
        </p>

        <PPH3>6.5 Identifiers</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We aim to avoid unnecessary correlation IDs in free text; structured offline tasks may still bundle JSON
          fields with context when needed for fidelity, audit, or reliability.
        </p>

        <PPH3>6.6 Tenant Isolation</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          One customer&apos;s meeting content is not used to produce another customer&apos;s outputs; context is scoped to
          the relevant tenant and meeting surfaces.
        </p>

        <PPH3>6.7 Vendor Retention</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We use contractual and technical arrangements to narrow reuse; vendor-specific configurations (such as API
          no-retention or reduced retention offerings) depend on commercially available programs.
        </p>

        <PPH3>6.8 Telemetry</PPH3>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We record operational telemetry for model traffic (for example vendor, task type classification, model
          identifier, timestamps, latency, tokens when vendors return them, request/correlation IDs, and meeting
          identifiers in structured logs) for reliability, metering, governance, not line-by-line content review.
          Richer categorical metadata may evolve over time.
        </p>
      </section>

      <section>
        <PPH2>7. What We Do Not Do</PPH2>
        <ul className="my-4 space-y-2">
          <PPItem>sell Customer Content;</PPItem>
          <PPItem>
            treat undisclosed vendor model training on Customer Content as acceptable: we align deployments and
            contracts to minimize unintended reuse;
          </PPItem>
          <PPItem>cross-customer leakage of outputs;</PPItem>
          <PPItem>
            blanket disclosure to every subprocessor: only actors needed for activated features participate;
          </PPItem>
          <PPItem>silent application of deferred learning outputs where product design expects guardrails;</PPItem>
          <PPItem>
            knowingly disclose Customer Content outside this Policy, lawful service delivery, authorization, or legal
            compulsion.
          </PPItem>
        </ul>
      </section>

      <section>
        <PPH2>8. How We Share Information</PPH2>
        <ul className="my-4 space-y-3 text-gray-600">
          <li className="leading-relaxed">
            <span className="font-semibold text-gray-900">Customer and authorized users </span>
            according to RBAC and org settings.
          </li>
          <li className="leading-relaxed">
            <span className="font-semibold text-gray-900">Providers and subprocessors </span>
            under contracts, for meetings, bots, speech, AI, calendar, infra, DB, queues, mail, observability,
            support, advisers.
          </li>
          <li className="leading-relaxed">
            <span className="font-semibold text-gray-900">Integrations </span>
            enabled by customers.
          </li>
          <li className="leading-relaxed">
            <span className="font-semibold text-gray-900">Legal, safety, compliance </span>
            when reasonably necessary.
          </li>
          <li className="leading-relaxed">
            <span className="font-semibold text-gray-900">Business transfers </span>
            subject to confidentiality and safeguards.
          </li>
        </ul>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Enterprise subprocessor schedules or DPAs may exist separately; contact{' '}
          <a href="mailto:legal@rithmo.ai" className="text-primary-600 hover:underline">
            legal@rithmo.ai
          </a>
          .
        </p>
      </section>

      <section>
        <PPH2>9. Retention and Deletion</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We retain data as needed to operate the service, meet legal/regulatory obligations, resolve disputes,
          enforce contracts, secure systems, and honor customer-controlled settings. Raw meeting artifacts may live
          for shorter periods than Derived outputs used for continuity. Credentials exist while integrations run.
          Logs roll per operational schedule. Deleted data may persist briefly in backups until overwritten unless
          law/contract mandates earlier handling.
        </p>
      </section>

      <section>
        <PPH2>10. Customer Controls</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Depending on plan and configuration, admins may tune recording/transcription/bots/analysis, scopes,
          retention defaults, integrations, AI usage, sensitivity policies, access levels. Administrators align with
          their internal policies.
        </p>
      </section>

      <section>
        <PPH2>11. Security</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">Measures may include:</p>
        <ul className="my-4 space-y-2">
          <PPItem>encryption in transit and at rest where supported;</PPItem>
          <PPItem>application-layer encryption for selected credentials/content;</PPItem>
          <PPItem>access controls, auth, token handling;</PPItem>
          <PPItem>structured logging within the bounds described in Sections 4.4 and 6.8;</PPItem>
          <PPItem>secure SDLC, monitoring, incident response.</PPItem>
        </ul>
        <p className="mb-4 text-gray-600 leading-relaxed">No system is perfectly secure. Protect credentials.</p>
      </section>

      <section>
        <PPH2>12. International Transfers</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Processing may occur in the United States and other countries where we or subprocessors operate. Laws may
          differ from your home jurisdiction. Where required we use appropriate safeguards: contractual clauses,
          DPAs, SCCs, or other lawful mechanisms. Regional hosting or data residency may be available under
          enterprise agreements; ask your account contact or{' '}
          <a href="mailto:legal@rithmo.ai" className="text-primary-600 hover:underline">
            legal@rithmo.ai
          </a>
          .
        </p>
      </section>

      <section>
        <PPH2>13. Your Privacy Rights</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Depending on location you may access, correct, delete, object/restrict, port, withdraw consent where
          applicable, or complain to regulators. Organizational users: we may coordinate with your admin.
        </p>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Contact{' '}
          <a href="mailto:privacy@rithmo.ai" className="text-primary-600 hover:underline">
            privacy@rithmo.ai
          </a>
          . We may verify identity before responding.
        </p>
      </section>

      <section>
        <PPH2>14. Organizational Users</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Your employer/host may decide whether Rithmo runs, what is captured, retention, integrations, outputs,
          audiences. Employees should contact that organization first.
        </p>
      </section>

      <section>
        <PPH2>15. Children</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          Rithmo targets business audiences and is not directed to children under 13 (United States COPPA framing)
          nor to knowingly collect children&apos;s data outside lawful, customer-directed enterprise contexts where the
          customer bears compliance duties. Parents or guardians who believe information was submitted in error
          should contact{' '}
          <a href="mailto:privacy@rithmo.ai" className="text-primary-600 hover:underline">
            privacy@rithmo.ai
          </a>
          .
        </p>
      </section>

      <section>
        <PPH2>16. Changes</PPH2>
        <p className="mb-4 text-gray-600 leading-relaxed">
          We may update this policy; we will revise the Last updated date. Material changes may get additional notice
          in-product, by email, or via administrators. Continued use after effectiveness applies the revision subject
          to law and contracts.
        </p>
      </section>

      <section>
        <PPH2>17. Contact</PPH2>
        <ul className="my-4 space-y-3 text-gray-600">
          <li>
            <span className="font-semibold text-gray-900">Rithmo, Inc.</span>
            <span className="block mt-1">
              Postal address: available upon written request via{' '}
              <a href="mailto:privacy@rithmo.ai" className="text-primary-600 hover:underline">
                privacy@rithmo.ai
              </a>{' '}
              until a public mailing address is published here.
            </span>
          </li>
          <li>
            <span className="font-semibold text-gray-900">Privacy: </span>
            <a href="mailto:privacy@rithmo.ai" className="text-primary-600 hover:underline">
              privacy@rithmo.ai
            </a>
          </li>
          <li>
            <span className="font-semibold text-gray-900">Legal / DPAs: </span>
            <a href="mailto:legal@rithmo.ai" className="text-primary-600 hover:underline">
              legal@rithmo.ai
            </a>
          </li>
          <li>
            <span className="font-semibold text-gray-900">Security: </span>
            <a href="mailto:security@rithmo.ai" className="text-primary-600 hover:underline">
              security@rithmo.ai
            </a>
          </li>
        </ul>
      </section>

      <section>
        <PPH2>Appendix A: AI and External Processing (summary)</PPH2>
        <div className="overflow-x-auto my-8 rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-gradient-to-r from-primary-50 to-accent-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Category</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Example data</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Purpose</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Posture</th>
              </tr>
            </thead>
            <tbody>
              <AppendixTableRow cols={['Meeting platforms', 'IDs, join details, participant metadata, lifecycle events', 'In-meeting operations', 'Customer-scoped access']} />
              <AppendixTableRow cols={['Bots / transcript ingestion', 'Join details, bot status, labels, transcript events', 'Capture & routing', 'Vendor contracts']} />
              <AppendixTableRow cols={['Speech-to-text', 'Audio, hypotheses, transcript text', 'Transcription', 'API terms/retention']} />
              <AppendixTableRow cols={['TTS', 'Generated speech text, settings, audio', 'Host audio', 'Minimize unrelated text']} />
              <AppendixTableRow cols={['Model inference', 'Prompts, excerpted transcript, agenda context', 'Summaries, extraction, classification', 'Enterprise terms + minimization']} />
              <AppendixTableRow cols={['Embeddings', 'Permitted text windows', 'Retrieval', 'Path-specific controls']} />
              <AppendixTableRow cols={['Document intelligence', 'Docs, extracted text, briefs', 'Workflows', 'Feature-gated']} />
              <AppendixTableRow cols={['Calendar', 'Tokens, event metadata', 'Scheduling', 'OAuth + encryption']} />
              <AppendixTableRow cols={['Messaging/email', 'Recipients, content, attachments', 'Delivery', 'Integration-scoped']} />
              <AppendixTableRow cols={['Infra', 'App data, metadata', 'Operations', 'Deployment controls']} />
              <AppendixTableRow cols={['Monitoring', 'Logs/metrics/errors', 'Reliability/security', 'Content minimization varies']} />
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <PPH2>Appendix B: Data Categories (summary)</PPH2>
        <div className="overflow-x-auto my-8 rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-gradient-to-r from-primary-50 to-accent-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Category</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Examples</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Sensitivity</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-200 align-top">Default handling</th>
              </tr>
            </thead>
            <tbody>
              <AppendixTableRow cols={['Account', 'Name, email, role, org', 'Medium', 'Auth, support, admin']} />
              <AppendixTableRow cols={['Meeting metadata', 'Title, time, attendees, IDs', 'High', 'Scoped + minimized vendors']} />
              <AppendixTableRow cols={['Meeting content', 'Audio, transcript, chat, uploads', 'Critical', 'Feature-gated; not arbitrary desktop capture unless explicitly supported']} />
              <AppendixTableRow cols={['Transcript segments', 'Text, labels, times', 'Critical', 'Path-dependent redaction']} />
              <AppendixTableRow cols={['Derived outputs', 'Summaries, decisions, scorecards', 'High', 'Protected like content']} />
              <AppendixTableRow cols={['Behavioral intelligence', 'Profiles, recommendations, longitudinal', 'High', 'Tenant-scoped']} />
              <AppendixTableRow cols={['Integration creds', 'OAuth/API tokens', 'Critical', 'Encrypted + limited access']} />
              <AppendixTableRow cols={['Telemetry', 'Latency, tokens, task types', 'Low if content-free', 'Ops + metering']} />
              <AppendixTableRow cols={['Pseudonym aids', 'Alias tables for prompting', 'Critical', 'Kept in trust boundary; coupling with vendors varies by path']} />
            </tbody>
          </table>
        </div>
      </section>
    </article>
  )
}
